import type { NumericRange, XyzDataPoint } from '../types';

/**
 * Scanned rather than spread into `Math.min`/`Math.max`.
 *
 * `Math.min(...values)` pushes one argument per element onto the call stack and throws
 * `RangeError` somewhere past a hundred thousand of them. Every array reduced here is one entry per
 * plotted component, so a large enough estate would take the chart from slow to thrown - and the
 * paint runs inside a `requestAnimationFrame` callback, where the throw escapes rather than
 * reaching a boundary. A reduce has no such ceiling and is not measurably slower at these sizes.
 *
 * Both return the identity for an empty array, matching what the spread form did.
 */
export const minOf = (values: number[]): number =>
  values.reduce((low, value) => (value < low ? value : low), Infinity);

export const maxOf = (values: number[]): number =>
  values.reduce((high, value) => (value > high ? value : high), -Infinity);

/**
 * The span of one dimension across the plotted points.
 *
 * Nulls are skipped rather than read as zero: a point with no radius has no radius, and folding it
 * in as 0 would drag the low end of the scale to a value nothing actually holds.
 */
export const rangeOf = (
  points: XyzDataPoint[],
  read: (point: XyzDataPoint) => number | null
): NumericRange => {
  const values = points
    .map(read)
    .filter((value): value is number => value !== null);
  if (values.length === 0) return [0, 1];
  const [min, max] = [minOf(values), maxOf(values)];

  /* A single distinct value would otherwise give a zero-width range, which normalises to
     NaN and loses the whole plot rather than one axis. */
  return min === max ? [min - 0.5, max + 0.5] : [min, max];
};

/**
 * The span the colour ramp has to cover.
 *
 * Reads `colorValue ?? z` because that is the expression the renderers colour by, and the two have
 * to be the same or the scale is in different units from the values fed to it. That mismatch is
 * invisible at runtime - `ColorScale.forValue` clamps - so a cost field against a 1-5 score would
 * simply saturate every mark at the end of the ramp and encode nothing.
 *
 * Spans only the points that reach the ramp. With a colour field selected that is all of them; with
 * none, it is the ones whose type carries no colour and so fall back to z - and including the rest
 * would let components that are not on the ramp stretch it, compressing the ones that are. Same
 * rule either way: the range and the population it is built for have to be the same one.
 */
/**
 * Whether any component carries a value for the colour field.
 *
 * One derivation, because three places act on the answer and they have to agree: the renderer
 * decides whether a mark without one is drawn as unmeasured, {@link toColorRange} decides which
 * population the ramp spans, and the chart's accessible name decides whether to mention a colour
 * channel at all. Written out three times they agreed by inspection.
 *
 * Here rather than beside the renderer, which is where it first landed: this is a question about
 * the data, `toColorRange` is its nearest reader, and a `data/` module importing from `render/`
 * points the dependency the wrong way round.
 */
export const hasColorField = (points: XyzDataPoint[]): boolean =>
  points.some(({ colorValue }) => colorValue !== null);

export const toColorRange = (points: XyzDataPoint[]): NumericRange => {
  if (hasColorField(points)) {
    /* Only the components that have a value for the field. One that does not is drawn as
       unmeasured rather than as its height, so folding its z in here would stretch the range with
       a number in another unit - and the renderer would then map the whole field into a corner of
       the ramp. */
    return rangeOf(
      points.filter(({ colorValue }) => colorValue !== null),
      ({ colorValue }) => colorValue as number
    );
  }

  const onRamp = points.filter(({ fill }) => fill === null);
  return rangeOf(onRamp.length > 0 ? onRamp : points, ({ z }) => z);
};

export const isSameRange = (left: NumericRange, right: NumericRange): boolean =>
  left[0] === right[0] && left[1] === right[1];
